// Copyright (c) Docugami, Inc. All rights reserved.

import axios from "axios";
import { Config } from "config";
import { Logger, LoggerCategory } from "utils/Logger";
import { SubscriptionType } from 'networking/OrganizationApi';
import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { createDgBaseQuery } from "store/baseQuery";

export interface TierOption {
    name: string;
    type: SubscriptionType;
    price: number;
    priceId: string;
    description: string[];
}

export async function getCheckoutSession(organizationId: string, priceId: string) {
    const url = `${Config.billingApiUrl}/Payments/CreateCheckoutSession/${organizationId}/${priceId}`;
    const headers = {
        "Content-Type": 'application/json'
    }

    await axios.post(
        url,
        {
            headers
        },
    ).then(response => {
        window.location.href = response.data
    })
        .catch(err => {
            Logger.LogError(LoggerCategory.Billing, `failed to open Checkout session for org ${organizationId}`, err);
            window.location.href = '/oops';
        });
}

export async function redirectToCustomerPortal(organizationId: string): Promise<void> {
    const url = `${Config.billingApiUrl}/Payments/CustomerPortal/${organizationId}`;
    const headers = {
        "Content-Type": 'application/json'
    }

    await axios.post(
        url,
        { headers }
    ).then(response => {
        window.location.href = response.data
    })
        .catch(err => {
            Logger.LogError(LoggerCategory.Billing, `failed to open Stripe Customer Portal for org ${organizationId}`, err);
            window.location.href = '/oops';
        });
}

const billingBaseQuery = createDgBaseQuery(`${Config.billingApiUrl}`);
export const billingApiSlice = createApi({
    reducerPath: 'billingApi',
    baseQuery: billingBaseQuery,
    endpoints: builder => ({
        getSubscriptionTiers: builder.query<TierOption[], void>({
            query: () => '/Payments/Tiers',
            extraOptions: { maxRetries: 3 }
        }),
        getCheckoutSessionRedirect: builder.query<string, {orgId: string, priceId: string}>({
            query: ({ orgId, priceId }) => `/Payments/CreateCheckoutSession/${orgId}/${priceId}`
        }),
        getCustomerPortalRedirect: builder.query<string, string>({
            query: (orgId: string) => `/Payments/CustomerPortal/${orgId}`,
            extraOptions: { maxRetries: 3 }
        })
    })
});

export const {
    useGetSubscriptionTiersQuery,
    useGetCheckoutSessionRedirectQuery,
    useGetCustomerPortalRedirectQuery
} = billingApiSlice
